import { cloneDeep, isEqual, uniqWith } from "lodash";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { FlyoutFooter } from "@app/shared/Flyouts";
import { Modal } from "@app/shared/Modals";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons"; 
import React, { FC, useEffect, useRef, useState } from "react";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import { AccessReportsColumn } from "@app/access-reports/shared/accessReportsColumn";
import { AccessReportsWizardGroupPropertyIdEnum } from "@app/access-reports/shared/accessReportsWizardGroupPropertyIdEnum";
import { AccessReportsWizardOption } from "@app/access-reports/shared/accessReportsWizardOption";
import { ColumnDefaults } from "@app/access-reports/shared/columnDefaults";
import { SelectedColumnsByFilter } from "@app/access-reports/shared/selectedColumnsByFilter";

interface EditPrintColumnsProps {
  accessFilters: AccessReportsWizardOption[];
  columnDefaults: ColumnDefaults;
  columns: AccessReportsColumn[];
  disableButtons: boolean;
  groupPropertyId: AccessReportsWizardGroupPropertyIdEnum;
  hideSidePanel: () => void;
  onSaveColumnsClick: (groupPropertyId: AccessReportsWizardGroupPropertyIdEnum, columnDefaults: ColumnDefaults) => void;
}

const EditPrintColumns: FC<EditPrintColumnsProps> = (props: EditPrintColumnsProps) => {
  const accessFiltersButtonRef = useRef<HTMLButtonElement>(null);
  const [accessFilterDropdownVisible, setAccessFilterDropdownVisible] = useState(false);
  const [columnClearConfirmationVisible, setColumnClearConfirmationVisible] = useState(false);
  const [columnDefaults, setColumnDefaults] = useState<ColumnDefaults>(null);
  const [lastAddedFilterId, setLastAddedFilterId] = useState<number>(null);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedAccessFilters, setSelectedAccessFilters] = useState<AccessReportsWizardOption[]>([]);

  useEffect(() => {
    extractColumns();

    return () => {
      document.body.removeEventListener("click", onMouseClick);
    }
  }, []);

  useEffect(() => {
    extractColumns();
  }, [props.columnDefaults]);

  useEffect(() => {
    if (accessFilterDropdownVisible) {
      document.body.addEventListener("click", onMouseClick);
    } else {
      document.body.removeEventListener("click", onMouseClick);
    }
  }, [accessFilterDropdownVisible]);

  const buildColumnsCheckboxes = (): React.ReactNode => {
    if ((selectedAccessFilters.length > 0 || (props.groupPropertyId === AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardEmployeeCountPage || AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages)) && !!columnDefaults) {
      const columnTypes: string[] = [];
      for (const column of props.columns) {
        if (!columnTypes.includes(column.columnType)) {
          columnTypes.push(column.columnType);
        }
      }

      if (columnTypes.length) {
        return (
          <React.Fragment>
            <div className="row">
              <div className="col-12">
                <QESCheckbox
                  checked={selectAll}
                  containerClassName="mt-3 mb-0"
                  disabled={props.disableButtons}
                  label="Select All"
                  testId="access-reports-admin__print-columns--select-all-checkbox"
                  onChange={() => onColumnToggle()}
                />
              </div>
            </div>

            {columnTypes.map((columnType, columnTypeIndex) => {
              const columns: AccessReportsColumn[] = props.columns.filter(x => x.columnType === columnType);

              return (
                <React.Fragment key={columnTypeIndex}>
                  <div className="row">
                    <div className="col-12">
                      <legend className="mt-3 mb-0">
                        {columnType}
                      </legend>
                    </div>
                  </div>

                  {columns.map((column, columnIndex) => (
                    <div className="row" key={`${columnTypeIndex}-${columnIndex}`}>
                      <div className="col-12">
                        <QESCheckbox
                          checked={getColumnValue(column.id)}
                          disabled={props.disableButtons}
                          label={column.displayName ? column.displayName : column.name}
                          testId={`access-reports-admin__print-columns--checkbox-${columnTypeIndex}-${columnIndex}`}
                          onChange={() => onColumnToggle(column.id)}
                        />
                      </div>
                    </div>
                  ))}
                </React.Fragment>
              );
            })}
          </React.Fragment>
        );
      }
    }

    return (
      <div className="row">
        <div className="col-12">
          <p>
            <QesIcon iconName={QesIcons.AlertHexagon} description="Error" className="text-danger me-1 mb3" />
            <span className="text-muted">
              Error
            </span>
          </p>
        </div>
      </div>
    );
  }

  const extractColumns = (): void => {
    if (!!props.columnDefaults) {
      const newColumnDefaults: ColumnDefaults = cloneDeep(props.columnDefaults);
      let newSelectedAccessFilters: AccessReportsWizardOption[] = selectedAccessFilters;
      const selectedColumns: SelectedColumnsByFilter = newColumnDefaults.selectedColumns.find(x => x.filterId === 0);

      if (!!props.accessFilters?.length &&
        (props.groupPropertyId !== AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardEmployeeCountPage && props.groupPropertyId !== AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages)
      ) {
        // count pages do not have an access filter
        newSelectedAccessFilters = [props.accessFilters[0]];
      }

      setColumnDefaults(newColumnDefaults);
      setSelectAll(selectedColumns?.columns.length === props.columns.length);
      setSelectedAccessFilters(newSelectedAccessFilters);
    }
  }

  const getColumnValue = (columnId: number): boolean => {
    let filter = 0;

    if (props.groupPropertyId !== AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardEmployeeCountPage && props.groupPropertyId !== AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages) {
      filter = selectedAccessFilters[0].id;
    }

    const selectedColumnsByFilter: SelectedColumnsByFilter = columnDefaults.selectedColumns.find(x => x.filterId === filter);

    if (selectedColumnsByFilter?.columns.includes(columnId)) {
      return true;
    }

    return false;
  }

  const hideColumnClearConfirmation = (columnClearConfirmed: boolean): void => {
    if (columnClearConfirmed) {
      // clear columns for all currently-selected filters
      const newColumnDefaults: ColumnDefaults = cloneDeep(columnDefaults);
      const filteredSelectedColumns = newColumnDefaults.selectedColumns.filter(x => selectedAccessFilters.map(x => x.id).includes(x.filterId));

      for (const selectedColumns of filteredSelectedColumns) {
        selectedColumns.columns = [];
      }

      setColumnClearConfirmationVisible(false);
      setColumnDefaults(newColumnDefaults);
      setSelectAll(false);
    } else {
      // if the column clear is not confirmed, deselect the last filter that was selected
      const newSelectedAccessFilters: AccessReportsWizardOption[] = cloneDeep(selectedAccessFilters);

      setColumnClearConfirmationVisible(false);
      setSelectedAccessFilters(newSelectedAccessFilters.filter(x => x.id !== lastAddedFilterId));
    }
  }

  const onAccessFilterChange = (accessFilter: AccessReportsWizardOption): void => {
    let newColumnClearConfirmationVisible = false;
    let newSelectedAccessFilters: AccessReportsWizardOption[] = cloneDeep(selectedAccessFilters);

    if (newSelectedAccessFilters.map(x => x.id).includes(accessFilter.id)) {
      newSelectedAccessFilters = newSelectedAccessFilters.filter(x => x.id !== accessFilter.id);
    } else {
      newSelectedAccessFilters.push(accessFilter);
    }

    // determine whether the select all checkbox should be checked
    let filterId = 0;

    if (props.groupPropertyId !== AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardEmployeeCountPage && props.groupPropertyId !== AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages) {
      filterId = newSelectedAccessFilters[0]?.id;
    }

    const selectedColumns: SelectedColumnsByFilter = columnDefaults.selectedColumns.find(x => x.filterId === filterId);

    // compare each selected filter's selected columns:
    const filteredSelectedColumns: SelectedColumnsByFilter[] = columnDefaults.selectedColumns.filter(x => newSelectedAccessFilters.map(x => x.id).includes(x.filterId));
    // the order of the columns array does not matter; sort to prevent "differences"
    const unique = uniqWith(filteredSelectedColumns.map(x => x.columns.sort((n1, n2) => n1 - n2)), isEqual);

    // if there is a difference, then prompt the column clear confirmation modal
    if (unique.length > 1) {
      newColumnClearConfirmationVisible = true;
    }

    setColumnClearConfirmationVisible(newColumnClearConfirmationVisible);
    setLastAddedFilterId(accessFilter.id);
    setSelectAll(selectedColumns?.columns.length === props.columns.length);
    setSelectedAccessFilters(newSelectedAccessFilters);
  }

  const onColumnToggle = (columnId?: number): void => {
    const newColumnDefaults: ColumnDefaults = cloneDeep(columnDefaults);
    let newSelectAll = selectAll;

    const toggleColumn = (filterId: number): void => {
      let selectedColumns: SelectedColumnsByFilter = newColumnDefaults.selectedColumns.find(x => x.filterId === filterId);

      if (!selectedColumns) {
        selectedColumns = {
          columns: [],
          filterId: filterId,
        }

        newColumnDefaults.selectedColumns.push(selectedColumns);
      }

      if (!!columnId) {
        // individual checkbox toggle
        if (selectedColumns.columns.includes(columnId)) {
          selectedColumns.columns = selectedColumns.columns.filter(x => x !== columnId);
        } else {
          selectedColumns.columns.push(columnId);
        }
      } else {
        // select all toggle
        if (newSelectAll) {
          selectedColumns.columns = [];
        } else {
          selectedColumns.columns = props.columns.map(x => x.id);
        }
      }

      if (selectedColumns.columns.length === 0) {
        // removing the node if it doesn't contain columns in order to preserve template space
        newColumnDefaults.selectedColumns.filter(x => x.filterId === filterId);
      }

      newSelectAll = selectedColumns.columns.length === props.columns.length;
    }

    if (props.groupPropertyId === AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardEmployeeCountPage || props.groupPropertyId === AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages) {
      toggleColumn(0);
    } else {
      for (const filter of selectedAccessFilters) {
        toggleColumn(filter.id);
      }
    }

    setColumnDefaults(newColumnDefaults);
    setSelectAll(newSelectAll);
  }

  const onMouseClick = (e: MouseEvent): void => {
    const target: HTMLElement = e.target as HTMLElement;

    if (target.closest(".checkbox-dropdown-container") === null && accessFilterDropdownVisible) {
      setAccessFilterDropdownVisible(false);
    }
  }

  const updateAccessFilterDropdownVisible = (): void => {
    setAccessFilterDropdownVisible(true);
  }

  return (
    <React.Fragment>
      <Modal
        isVisible={columnClearConfirmationVisible}
        content={{
          body: "Selecting multiple access filters with different selected columns will clear all column selections for those filters. Do you wish to continue?",
          cancelButtonText: "Cancel",
          confirmButtonAction: () => hideColumnClearConfirmation(true),
          confirmButtonText: "Continue",
          confirmButtonType: "primary",
          title: "Warning",
        }}
        closeModal={() => hideColumnClearConfirmation(false)}
        triggerElement={accessFiltersButtonRef}
      />

      <div className="row">
        <div className="col-12">
          <QESAlert classNames="mb-0" type="info" persist={true}>
            There can only be a limited number of columns on a printed report.
          </QESAlert>
        </div>
      </div>

      {(props.groupPropertyId === AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardEmployeeCountPage || props.groupPropertyId === AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages) ?
        buildColumnsCheckboxes() :
        props.accessFilters.length > 0 && (
          <React.Fragment>
            <div className="row">
              <div className="col-12">
                <div className="form-group mt-3 mb-0">
                  <legend className="mb-0">
                    Access Filter
                  </legend>

                  <div className="position-relative checkbox-dropdown-container">
                    <QESButton
                      qesTestId="access-reports-admin__print-columns--access-filters"
                      qesType="secondary"
                      className="d-flex h-100 w-100 align-items-center justify-content-between access-reports-groups-btn"
                      onClick={() => updateAccessFilterDropdownVisible()}
                      qesRef={accessFiltersButtonRef}
                    >
                      <span>
                        {(selectedAccessFilters.length > 0) ?
                          selectedAccessFilters.map(x => !!x.displayName ? x.displayName : x.name).join(", ") :
                          "\u00A0"
                        }
                      </span>
                      <span aria-hidden="true"><QesIcon iconName={QesIcons.ChevronDown} description="Expand" /></span>
                    </QESButton>
                    {accessFilterDropdownVisible && (
                      <div className="checkbox-dropdown mt-0 w-100">
                        {props.accessFilters.map((accessFilter, accessFilterIndex) => (
                          <QESCheckbox
                            containerClassName="checkbox-dropdown__label cursor-pointer"
                            label={!!accessFilter.displayName ? accessFilter.displayName : accessFilter.name}
                            checked={selectedAccessFilters.map(x => x.id).includes(accessFilter.id)}
                            className="checkbox-dropdown__checkbox"
                            onChange={() => onAccessFilterChange(accessFilter)}
                            key={accessFilterIndex}
                            testId={`access-reports-admin__print-columns--access-filters-${accessFilter.id}`}
                          />
                        ))}

                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {selectedAccessFilters.length > 0 &&
              buildColumnsCheckboxes()
            }
          </React.Fragment>
        )}

      <FlyoutFooter
        submitButton={
          <QESButton
            className="cursor-pointer ms-3"
            disabled={props.disableButtons}
            onClick={() => props.onSaveColumnsClick(props.groupPropertyId, columnDefaults)}
            qesTestId="access_reports_admin__print-columns--save"
            qesType="primary"
          >
            Save
          </QESButton>
        }
      />
    </React.Fragment>
  );
}

export { EditPrintColumns };
